/**
 * Central registry of every Mixpanel event name fired from dash-web.
 *
 * This is step one toward type-checked analytics: a single source of truth for
 * event names, grouped by product domain. Reference these constants instead of
 * inlining string literals, e.g.
 *
 *   trackingStore.track(ANALYTICS_EVENTS.CAMPAIGNS.CAMPAIGNS_FILTER, { ... });
 *
 * `track()` (and friends) accept `AnalyticsEventName | (string & {})`, so the
 * constants give autocomplete/validation without breaking the many existing
 * call sites that still pass raw strings or dynamically-built names. Tightening
 * the signature to the strict union is a deliberate follow-up.
 *
 * When adding a new event: add it to the relevant group below (keep keys in
 * SCREAMING_SNAKE_CASE derived from the value) rather than inlining a literal.
 */
export const ANALYTICS_EVENTS = {
  AFFILIATE: {
    ADD_AFFILIATE_LINK_AFTER_DISCARD: 'Add Affiliate Link After Discard',
    AFFILIATE_FORM_UTM_PARAMETERS_TOGGLED: 'Affiliate Form - UTM Parameters Toggled',
    AFFILIATE_LINK_COPIED: 'Affiliate Link Copied',
    AFFILIATE_LINK_GENERATED: 'Affiliate Link Generated',
    AFFILIATE_LINKS_TABLE_ADDITIONAL_LINK: 'Affiliate Links Table - Additional Link',
    AFFILIATE_LINKS_TABLE_FIRST_AFFILIATE_LINK_FORM:
      'Affiliate Links Table - First Affiliate Link Form',
    CREATE_AFFILIATE_LINK_FORM_FIELD_VALUE_ENTERED_EDITED:
      'Create affiliate link form field - value entered/edited',
    DISCARD_LINK_POPUP_OPENED: 'Discard link popup opened',
    EDIT_AFFILIATE_LINK_BUTTON_CLICK: 'Edit Affiliate Link Button Click',
    EDIT_AFFILIATE_LINK_FORM_FIELD_VALUE_ENTERED_EDITED:
      'Edit affiliate link form field - value entered/edited',
    LINK_DISCARDED: 'Link Discarded',
    LINKS_SAVED: 'Links Saved',
    PRODUCT_LINK_ADDED: 'Product Link Added',
    PRODUCT_LINK_UPDATED: 'Product Link Updated',
    VIEW_AFFILIATE_LINK_DETAILS: 'View Affiliate Link Details',
  },
  AUTH: {
    ACCOUNT_CREATION_ACCOUNT_CREATED: 'Account Creation - Account Created',
    ACCOUNT_CREATION_SET_PASSWORD: 'Account Creation - Set Password',
    ACCOUNT_SETUP_COMPLETED: 'Account Setup Completed',
    CHECK_TIMEZONE: 'Check Timezone',
    DEVELOPER_API_KEY_ACTION: 'Developer API Key Action',
    DEVELOPER_API_KEY_COPIED: 'Developer API Key Copied',
    INVITATION_RE_SENT: 'Invitation Re-sent',
    LOGGED_ON: 'Logged on',
    LOGIN: 'Login',
    LOGIN_FAILED: 'Login Failed',
    MFA_VERIFICATION: 'MFA Verification',
    MFA_VERIFICATION_FAILED: 'MFA Verification Failed',
    ONBOARDING_FLOW_QUESTIONS: 'Onboarding Flow Questions',
    PASSWORD_RESET: 'Password Reset',
    PASSWORD_RESET_FAILED: 'Password Reset Failed',
    PASSWORD_RESET_LINK_EXPIRED: 'Password Reset Link Expired',
    PASSWORD_RESET_SUCCESS: 'Password Reset Success',
    PERMISSIONS_UPDATED: 'Permissions Updated',
    RESET_PASSWORD: 'Reset Password',
    SET_PASSWORD_INTERACTION: 'Set Password Interaction',
    USER_ADDED: 'User Added',
    USER_REMOVED: 'User Removed',
  },
  BILLING: {
    BILLING_AND_PAYMENT_SOCIAL_LISTENING_UPGRADE_TOGGLE:
      'Billing And Payment - Social Listening Upgrade Toggle',
    BILLING_AND_PLAN_CONTACT_US: 'Billing And Plan - Contact Us',
    BILLING_AND_PLAN_PURCHASE: 'Billing And Plan - Purchase',
    BILLING_AND_PLAN_SUBSCRIBE_CLICKED: 'Billing And Plan - Subscribe Clicked',
    BILLING_AND_PAYMENT_BUTTON_CLICKED: 'Billing and Payment - Button Clicked',
    BILLING_AND_PAYMENT_PURCHASE_SUCCESS_CTA: 'Billing and Payment - Purchase Success CTA',
    BILLING_AND_PLAN_SUBSCRIBE_UPDATE: 'Billing and Plan - Subscribe Update',
    CHILI_PIPER: 'Chili Piper',
    START_FREE_TRIAL_RESULT: 'Start Free Trial Result',
    TRIAL_OVERVIEW_MODAL: 'Trial Overview Modal',
    UPGRADE_OPTIONS_BUTTON_CLICKED: 'Upgrade Options - Button Clicked',
  },
  CAMPAIGNS: {
    CAMPAIGN_OVERVIEW_FILTER: 'Campaign Overview Filter',
    CAMPAIGNS_ADD_TO_CAMPAIGN: 'Campaigns Add To Campaign',
    CAMPAIGNS_CONTENT_SELECTION: 'Campaigns Content Selection',
    CAMPAIGNS_CREATE_CAMPAIGN: 'Campaigns Create Campaign',
    CAMPAIGNS_DELETE_CAMPAIGN: 'Campaigns Delete Campaign',
    CAMPAIGNS_EDIT_CAMPAIGN: 'Campaigns Edit Campaign',
    CAMPAIGNS_FILTER: 'Campaigns Filter',
    CAMPAIGNS_REMOVE_FROM_CAMPAIGN: 'Campaigns Remove From Campaign',
    CAMPAIGNS_REPORT_DOWNLOAD_SELECTED: 'Campaigns Report Download Selected',
    CAMPAIGNS_VIEW_CAMPAIGN: 'Campaigns View Campaign',
    CREATE_CAMPAIGN_CLICKED: 'Create Campaign Clicked',
  },
  COMMENTING: {
    APPROVAL_SETTINGS_CHANGED: 'Approval Settings Changed',
    COMMENTING_ADDING_COMMENT: 'Commenting Adding Comment',
    COMMENTING_DELETING_COMMENT: 'Commenting Deleting Comment',
    COMMENTING_EDITING_COMMENT: 'Commenting Editing Comment',
    COMMENTING_PANEL_EXPANDED: 'Commenting Panel Expanded',
    CONTENT_RIGHTS_REQUESTED: 'Content Rights Requested',
    DISCARD_POST_POPUP: 'Discard Post Popup',
    LANDING_PAGE_CONTENT_SELECTION: 'Landing Page Content Selection',
    MESSAGE_ASSIGNED: 'Message Assigned',
    MESSAGE_RULES_SELECTED: 'Message Rules Selected',
  },
  COMMUNITY: {
    COMMUNITY_CREATE_EDIT_MESSAGE_RULE: 'Community - Create/Edit Message Rule',
    COMMUNITY_CUSTOM_VIEW_EDITED: 'Community - Custom View Edited',
    COMMUNITY_MESSAGE_RULE_CREATED: 'Community - Message Rule Created',
    COMMUNITY_MESSAGE_RULE_EDITED: 'Community - Message Rule Edited',
    COMMUNITY_MESSAGE_RULE_INTERACTIONS: 'Community - Message Rule Interactions',
    COMMUNITY_MESSAGE_RULE_PREVIEW: 'Community - Message Rule Preview',
    COMMUNITY_SELECT_MESSAGE_FILTERS_AND_ACTIONS: 'Community - Select Message Filters and Actions',
    COMMUNITY_UNDO_MESSAGE_RULE: 'Community - Undo Message Rule',
    COMMUNITY_BLOCK_USER: 'Community Block User',
    COMMUNITY_COMMENT_POPUP: 'Community Comment Popup',
    COMMUNITY_CONVERSATION_INTERACTION: 'Community Conversation Interaction',
    COMMUNITY_MESSAGE_INTERACTION: 'Community Message Interaction',
    COMMUNITY_POST_LEVEL_UPDATE: 'Community Post Level Update',
    COMMUNITY_PRESET_REPLIES: 'Community Preset Replies',
    COMMUNITY_REPORT_INTERACTION: 'Community Report Interaction',
    COMMUNITY_TRANSLATE_MESSAGE: 'Community Translate Message',
    COMMUNITY_TRANSLATE_REPLY: 'Community Translate Reply',
    COMMUNITY_UNBLOCK_USER: 'Community Unblock User',
  },
  COMPETITIVE: {
    ADD_COMPETITOR_CLICKED: 'Add Competitor Clicked',
    CLICKED_ON_CREATOR_PROFILE_COMPETITORS_TAB: 'Clicked on Creator Profile - Competitors tab',
    CLICKED_THE_MENTIONS_SORT_OPTION_IN_THE_TOP_COMPETITOR_MENTIONS_TABLE:
      'Clicked the Mentions sort option in the Top Competitor Mentions table',
    COMPETITIVE_ACCOUNT_ADDED: 'Competitive Account Added',
    COMPETITIVE_ACCOUNT_REMOVED: 'Competitive Account Removed',
    COMPETITIVE_CONTENT_SELECTION: 'Competitive Content Selection',
    COMPETITIVE_OVERVIEW_FILTER: 'Competitive Overview Filter',
    COMPETITIVE_YOUR_POSTS_FILTER: 'Competitive Your Posts Filter',
    COMPETITOR_ACCOUNT_FILTERS: 'Competitor Account Filters',
    COMPETITOR_ACCOUNT_TAGS: 'Competitor Account Tags',
    COMPETITOR_VIEW_COMPETITOR: 'Competitor View Competitor',
    COMPETITOR_VIEW_TREND: 'Competitor View Trend',
    COMPETITOR_VISUAL_IQ_COMPETITIVE_FILTER: 'Competitor Visual IQ Competitive Filter',
    COMPETITOR_VISUAL_IQ_TRENDS_FILTER: 'Competitor Visual IQ Trends Filter',
    TREND_ACCOUNT_ADDED: 'Trend Account Added',
    TREND_ACCOUNT_FILTERS: 'Trend Account Filters',
    TREND_ACCOUNT_REMOVED: 'Trend Account Removed',
    VIQ_COMPETITIVE_ACCOUNT_ADDED: 'VIQ Competitive Account Added',
    VIQ_COMPETITIVE_ACCOUNT_REMOVED: 'VIQ Competitive Account Removed',
  },
  CREATORS: {
    CONNECTION_INVITE_EMAIL_SENT: 'Connection Invite Email Sent',
    CONNECTION_INVITE_LINK_COPIED: 'Connection Invite Link Copied',
    CREATOR_ABOUT_TAB_SAVED: 'Creator About Tab - Saved',
    CREATOR_ACCOUNT_PROFILE_VIEW: 'Creator Account Profile View',
    CREATOR_ADDED_TO_CAMPAIGN: 'Creator Added to Campaign',
    CREATOR_ADDED_TO_YOUR_CREATORS: 'Creator Added to Your Creators',
    CREATOR_CUSTOM_FIELD_DELETED: 'Creator Custom Field - Deleted',
    CREATOR_CUSTOM_FIELD_SAVED: 'Creator Custom Field - Saved',
    CREATOR_DELIVERABLES_BITLY_CONNECTION_BANNER_OPENED:
      'Creator Deliverables - Bitly Connection Banner Opened',
    CREATOR_DELIVERABLES_GA_CONNECTION_BANNER_CONNECT_BUTTON_CLICK:
      'Creator Deliverables - GA Connection Banner Connect Button Click',
    CREATOR_DETAIL_REQUEST_FORM_COPIED: 'Creator Detail Request Form - Copied',
    CREATOR_DETAIL_REQUEST_FORM_SUBMIT: 'Creator Detail Request Form - Submit',
    CREATOR_DISCOVERY_SEARCH: 'Creator Discovery Search',
    CREATOR_EMAIL_SENT: 'Creator Email Sent',
    CREATOR_EMAIL_THREAD_VIEWED: 'Creator Email Thread Viewed',
    CREATOR_FIT_DETAILED_SUMMARY_VIEW: 'Creator Fit Detailed Summary View',
    CREATOR_FIT_RISK_CLICK: 'Creator Fit Risk Click',
    CREATOR_FIT_SUMMARY_REFRESH: 'Creator Fit Summary Refresh',
    CREATOR_PROFILE_AUDIENCE_DEMOGRAPHICS_TAB_CHANGED:
      'Creator Profile Audience Demographics Tab Changed',
    CREATOR_PROFILE_ENGAGED_ACCOUNTS_DATE_RANGE_CHANGED:
      'Creator Profile Engaged Accounts Date Range Changed',
    CREATOR_PROFILE_ENGAGED_ACCOUNTS_LOCATION_TYPE_CHANGED:
      'Creator Profile Engaged Accounts Location Type Changed',
    CREATOR_PROFILE_OUTREACH_SEND_DM: 'Creator Profile Outreach Send DM',
    CREATOR_PROFILE_REACH_DATE_RANGE_CHANGED: 'Creator Profile Reach Date Range Changed',
    CREATOR_PROFILE_VIEW: 'Creator Profile View',
    DETAIL_REQUEST_LINK_INSERTED_INTO_EMAIL: 'Detail Request Link Inserted into Email',
    DETAILS_REQUEST_LINK_COPIED: 'Details Request Link Copied',
    DISCOVERY_CONTENT_SEARCH_SUBMIT: 'Discovery Content Search Submit',
    DISCOVERY_CREATOR_ADD: 'Discovery Creator Add',
    DISCOVERY_PROMPT_STARTER_CLICKED: 'Discovery Prompt Starter Clicked',
    DISCOVERY_SEARCH_SUBMITTED: 'Discovery Search Submitted',
    EMAIL_PREFERENCES_PREFERENCES_SAVED: 'Email Preferences - Preferences Saved',
    EMAIL_PREFERENCES_REPORT_SENT: 'Email Preferences - Report Sent',
    FIND_SIMILAR_CLICKED: 'Find Similar Clicked',
    INTERNAL_NOTE_CREATED: 'Internal Note Created',
    INTERNAL_NOTE_VIEWED: 'Internal Note Viewed',
    VIEWED_CREATOR_PROFILE_OUTREACH_TAB_CHANNEL: 'Viewed Creator Profile Outreach Tab Channel',
  },
  CREATOR_PAYMENTS: {
    CREATORS_ACCEPTS_PAY_TOS: 'Creators Accepts Pay TOS',
    CREATORS_CLICK_PAYMENTS_OVERVIEW_CTA: 'Creators Click Payments Overview CTA',
    CREATORS_CLICK_SETUP_PAYPAL: 'Creators Click SetUp PayPal',
    CREATORS_CLICKS_DEACTIVATE_PAYEE_STATUS: 'Creators Clicks Deactivate Payee Status',
    CREATORS_CLICKS_REACTIVATES_PAYEE_STATUS: 'Creators Clicks Reactivates Payee Status',
    CREATORS_COMPLETE_ADD_PAYEE: 'Creators Complete Add Payee',
    CREATORS_COMPLETE_PAYOR_PROFILE_STEP: 'Creators Complete Payor Profile Step',
    CREATORS_PAY_FROM_PAYEE_TABLE: 'Creators Pay from Payee Table',
    CREATORS_PAYEE_SEND_ACCESS_LINK: 'Creators Payee Send Access Link',
    CREATORS_REMOVED_FROM_YOUR_CREATORS: 'Creators Removed from Your Creators',
    CREATORS_START_ADD_PAYEE: 'Creators Start Add Payee',
    CREATORS_START_CREATOR_PAYMENT: 'Creators Start Creator Payment',
    CREATORS_START_DEPOSIT_FUNDS: 'Creators Start Deposit Funds',
    CREATORS_START_PAYOR_PROFILE_SETUP: 'Creators Start Payor Profile Setup',
    CREATORS_SUBMIT_CREATOR_PAYMENT: 'Creators Submit Creator Payment',
    CREATORS_SUBMIT_DEPOSIT_FUNDS: 'Creators Submit Deposit Funds',
    CREATORS_SUBMIT_PAYOR_PROFILE: 'Creators Submit Payor Profile',
    CREATORS_SUBMIT_REPEAT_PAYMENT: 'Creators Submit Repeat Payment',
    CREATORS_VIEW_PAY_TOS: 'Creators View Pay TOS',
    CREATORS_VIEW_PAYEES: 'Creators View Payees',
    CREATORS_VIEW_PAYMENT_HISTORY: 'Creators View Payment History',
  },
  DASHBOARDS: {
    DASHBOARD_DELETE_DASHBOARD: 'Dashboard Delete Dashboard',
    DASHBOARD_EDIT_DASHBOARD: 'Dashboard Edit Dashboard',
    DASHBOARD_REPORT_DATE_CHANGED: 'Dashboard Report Date Changed',
    DASHBOARD_SORT_COMPARISON_PERIOD: 'Dashboard Sort Comparison Period',
    DASHBOARD_SORT_REPORTING_PERIOD: 'Dashboard Sort Reporting Period',
    DASHBOARD_UNSUBSCRIBE_AUTO_EXPORT: 'Dashboard Unsubscribe Auto Export',
  },
  FACEBOOK: {
    FACEBOOK_LOCATION_TAGGED: 'Facebook Location Tagged',
    FACEBOOK_OVERVIEW_FILTER: 'Facebook Overview Filter',
    FACEBOOK_REACH_DEPRECATION_BANNER_DISMISSED: 'Facebook Reach Deprecation Banner Dismissed',
    FACEBOOK_VIDEO_THUMBNAIL_CHANGED: 'Facebook Video Thumbnail Changed',
  },
  FILTERS: {
    FILTER_DRAWER_INTERACTION: 'Filter Drawer Interaction',
    FILTER_INTERACTION: 'Filter Interaction',
    PERFORMANCE_FILTER_CHANGED: 'Performance Filter Changed',
    SEARCH_FILTER_CHANGED: 'Search Filter Changed',
    SETTINGS_ACCOUNT_FILTER: 'Settings Account Filter',
  },
  INSTAGRAM: {
    INSTAGRAM_ACCOUNT_S_TAGGED: 'Instagram Account(s) Tagged',
    INSTAGRAM_LOCATION_TAGGED: 'Instagram Location Tagged',
    INSTAGRAM_OVERVIEW_CONTENT_INSIGHTS: 'Instagram Overview Content Insights',
    INSTAGRAM_OVERVIEW_CONTENT_SELECTION: 'Instagram Overview Content Selection',
    INSTAGRAM_OVERVIEW_FILTERS: 'Instagram Overview Filters',
    INSTAGRAM_SHOPPING_PRODUCT_TAGGED: 'Instagram Shopping Product Tagged',
    INSTAGRAM_STORY_POST_TRACKABLE_LINK_CHECKBOX_CLICKED:
      'Instagram Story Post Trackable Link Checkbox Clicked',
    INSTAGRAM_VIDEO_THUMBNAIL_CHANGED: 'Instagram Video Thumbnail Changed',
  },
  INTEGRATIONS: {
    GOOGLE_ANALYTICS_ACCOUNT_ADDED: 'Google Analytics Account Added',
    GOOGLE_ANALYTICS_BANNER_START_FREE_TRIAL_BUTTON_CLICKED:
      'Google Analytics Banner - Start Free Trial Button Clicked',
    INTEGRATION_CONNECTED: 'Integration Connected',
    INTEGRATION_CONNECTION_FAILURE: 'Integration Connection Failure',
    INTEGRATION_DISCONNECTED: 'Integration Disconnected',
    INTEGRATION_WORKFLOW_CHANGE: 'Integration Workflow Change',
    META_AD_ACCOUNTS_EDIT: 'Meta Ad Accounts Edit',
    PLATFORM_CONNECTION_CALLBACK_SUCCESS: 'Platform Connection Callback Success',
    PLATFORM_CONNECTION_CLICKED: 'Platform Connection Clicked',
    PLATFORM_CONNECTION_EXPLORE_DH: 'Platform Connection Explore DH',
    PLATFORM_CONNECTION_NO_ACCOUNT_FOUND: 'Platform Connection No Account Found',
    PLATFORM_CONNECTION_POPUP: 'Platform Connection Popup',
    PLATFORM_CONNECTION_PREPARE_TO_CONNECT_INTERACTION:
      'Platform Connection Prepare To Connect Interaction',
    PLATFORM_CONNECTION_STATUS_UPDATE: 'Platform Connection Status Update',
    PLATFORM_DISCONNECTED: 'Platform Disconnected',
    SEND_GALLERY_FAILED: 'Send Gallery Failed',
    SEND_TO_INTEGRATION_FAILURE: 'Send to Integration Failure',
    SEND_TO_INTEGRATION_SELECTED: 'Send to Integration Selected',
    SENT_TO_INTEGRATION: 'Sent to Integration',
    STARTED_INTEGRATION_CONNECTION: 'Started Integration Connection',
  },
  LIKESHOP: {
    LIKESHOP_ADD_MEDIA: 'LikeShop Add Media',
    LIKESHOP_CLICK_PREVIEW: 'LikeShop Click Preview',
    LIKESHOP_CUSTOM_CONTENT_ADDED: 'LikeShop Custom Content Added',
    LIKESHOP_CUSTOM_CONTENT_CROPPED: 'LikeShop Custom Content Cropped',
    LIKESHOP_CUSTOMIZE_SAVED: 'LikeShop Customize Saved',
    LIKESHOP_CUSTOMIZE_SELECTED: 'LikeShop Customize Selected',
    LIKESHOP_REORDER_SAVED: 'LikeShop Reorder Saved',
    LIKESHOP_REORDER_SELECTED: 'LikeShop Reorder Selected',
    LIKESHOP_YOUR_POST_FILTER: 'LikeShop Your Post Filter',
  },
  MEDIA_LIBRARY: {
    ASSETS_DOWNLOADED: 'Assets Downloaded',
    BOARD_MEDIA_REMOVED: 'Board Media Removed',
    CROPPED_MEDIA_SAVED: 'Cropped media saved',
    GALLERY_CREATED_CONSTELLATION: 'Gallery Created - Constellation',
    LIBRARY_UGC_DELETED: 'Library UGC Deleted',
    LIBRARY_UPLOAD_CONTENT: 'Library upload content',
    MANAGE_TAGS: 'Manage Tags',
    MEDIA_ADDED_TO_INSTAGRAM_RIQ: 'Media Added To Instagram RIQ',
    MEDIA_DRAWER_CONNECT_CANVA: 'Media Drawer - Connect Canva',
    MEDIA_DRAWER_EDIT_IN_ADOBE_EXPRESS: 'Media Drawer - Edit in Adobe Express',
    MEDIA_DRAWER_EDIT_IN_CANVA: 'Media Drawer - Edit in Canva',
    MEDIA_DRAWER_TRY_ADOBE_EXPRESS: 'Media Drawer - Try Adobe Express',
    MEDIA_DRAWER_TRY_CANVA: 'Media Drawer - Try Canva',
    MEDIA_LIBRARY_BULK_ACTION: 'Media Library Bulk Action',
    MEDIA_LIBRARY_SEARCH: 'Media Library Search',
    MEDIA_POPUP_OPENED_FROM_TOP_POSTS: 'Media Popup Opened from Top Posts',
    MEDIA_SEARCHED: 'Media Searched',
    MEDIA_TAG_ADDED: 'Media Tag Added',
    MEDIA_TAG_REMOVED: 'Media Tag Removed',
    MEDIA_UPLOADED: 'Media Uploaded',
    MEDIA_ADDED_TO_UNSCHEDULED_POSTS: 'Media added to Unscheduled Posts.',
    OPEN_MEDIA_CROPPER: 'Open media cropper',
    REORDER_MEDIA_DRAG_AND_DROP_ACTION_MADE: 'Reorder Media Drag & Drop Action Made',
    SIMILAR_MEDIA_CLICKED: 'Similar Media Clicked',
    STORY_ADD_FRAMES: 'Story Add Frames',
    STORY_CREATE_NEW_STORY: 'Story Create New Story',
    STORY_OVERVIEW_CHANGE_GRAPH: 'Story Overview Change Graph',
    STORY_REMOVE_FRAMES: 'Story Remove Frames',
    TOP_POSTS_SORTED: 'Top Posts Sorted',
    VIEW_BOARD: 'View Board',
  },
  NAVIGATION: {
    CSV_DOWNLOADED: 'CSV Downloaded',
    DATA_IMPORTING_POPUP_CLOSED: 'Data Importing Popup Closed',
    EMPTY_STATE_CALL_TO_ACTION: 'Empty State - Call To Action',
    FILE_DOWNLOADED: 'File Downloaded',
    INFORMATION_HOVER: 'Information Hover',
    INTERCOM_CLOSE: 'Intercom Close',
    INTERCOM_OPEN: 'Intercom Open',
    NAVIGATION_HEADING_SELECTED: 'Navigation Heading Selected',
    NOTIFICATION_PANEL_OPTION_SELECTION: 'Notification Panel Option Selection',
    ONBOARDING_WATCH_VIDEO: 'Onboarding Watch Video',
    PREVIEW_BUTTON_CLICKED: 'Preview Button Clicked',
    REPORT_DOWNLOADED: 'Report Downloaded',
    REPORT_INTERACTION: 'Report Interaction',
    SECONDARY_NAV_INTERACTION: 'Secondary Nav Interaction',
    TEASER_PAGE_VIEW: 'Teaser Page View',
  },
  PAGE: {
    PAGE_VIEWED: 'Page Viewed',
  },
  PINTEREST: {
    PINTEREST_BOARD_CREATED: 'Pinterest Board Created',
    PINTEREST_ENGAGEMENT_DATE_RANGE_SET: 'Pinterest Engagement Date Range Set',
    PINTEREST_ENGAGEMENT_FILTER_SET: 'Pinterest Engagement Filter Set',
    PINTEREST_VIDEO_THUMBNAIL_CHANGED: 'Pinterest Video Thumbnail Changed',
  },
  PRODUCTS: {
    ADD_FACEBOOK_PRODUCT_CATALOG_CLICK: 'Add Facebook Product Catalog Click',
    ADD_PRODUCT_FEED_CLICK: 'Add Product Feed Click',
    PRODUCT_ARCHIVE_PRODUCT: 'Product Archive Product',
    PRODUCT_CSV_IMPORT_START: 'Product CSV Import - Start',
    PRODUCT_FEED_ARCHIVE_STATUS_CHANGED: 'Product Feed Archive Status Changed',
    PRODUCT_IMPORT_PRODUCT_FEED: 'Product Import Product Feed',
    PRODUCT_PAGE_GALLERY_CLICK: 'Product Page Gallery Click',
    PRODUCT_PAGE_GALLERY_SETTINGS_INTERACTION: 'Product Page Gallery Settings Interaction',
    PRODUCT_RESOURCES_BUTTONS: 'Product Resources Buttons',
    PRODUCT_URL_CLICKED: 'Product URL Clicked',
    PRODUCTS_OVERVIEW_FILTERS: 'Products Overview Filters',
    SHOPIFY_ADD_ORDER: 'Shopify Add Order',
    SHOPIFY_CREATOR_PROFILE_TAB: 'Shopify Creator Profile Tab',
    SHOPIFY_EDIT_CREATOR_ADDRESS: 'Shopify Edit Creator Address',
  },
  RELATIONSHIPS: {
    RELATIONSHIP_ADD_INFLUENCER: 'Relationship Add Influencer',
    RELATIONSHIP_ADD_TAG_TO_INFLUENCER: 'Relationship Add Tag to Influencer',
    RELATIONSHIP_CHANGE_STATUS: 'Relationship Change Status',
    RELATIONSHIP_COPY_INVITE: 'Relationship Copy Invite',
    RELATIONSHIP_EDIT_INVITE_MESSAGE: 'Relationship Edit Invite Message',
    RELATIONSHIP_FILTER_USED: 'Relationship Filter Used',
    RELATIONSHIP_FIND_INFLUENCER: 'Relationship Find Influencer',
    RELATIONSHIP_INFLUENCER_ACCEPT_INVITE: 'Relationship Influencer Accept Invite',
    RELATIONSHIP_INFLUENCER_CONNECTION_FAILED: 'Relationship Influencer Connection Failed',
    RELATIONSHIP_INFLUENCER_CONNECTION_SUCCESS: 'Relationship Influencer Connection Success',
    RELATIONSHIP_INFLUENCER_REVOKE_ACCEPTANCE: 'Relationship Influencer Revoke Acceptance',
    RELATIONSHIP_REMOVED_INFLUENCER: 'Relationship Removed Influencer',
    RELATIONSHIP_SAVE_AND_CONTINUE: 'Relationship Save and Continue',
    RELATIONSHIP_SEND_INVITE: 'Relationship Send Invite',
    RELATIONSHIP_TAB_SWITCH: 'Relationship Tab Switch',
    RELATIONSHIP_VIEW_INFLUENCER_ACCOUNT_STATS: 'Relationship View Influencer Account Stats',
  },
  SCHEDULER: {
    SCHEDULER_APPROVAL_BUTTON_CLICKED: 'Scheduler - Approval Button Clicked',
    SCHEDULER_APPROVAL_POPOUT: 'Scheduler - Approval Popout',
    SCHEDULER_APPROVAL_REQUEST_APPROVER: 'Scheduler - Approval Request Approver',
    SCHEDULER_UTM_CUSTOMIZE: 'Scheduler - UTM Customize',
    SCHEDULER_UTM_POPOUT: 'Scheduler - UTM Popout',
    SCHEDULER_ADD_BRAND_CLICKED: 'Scheduler Add Brand Clicked',
    SCHEDULER_ADD_CHANNEL_CLICKED: 'Scheduler Add Channel Clicked',
    SCHEDULER_ALT_TEXT_CLICKED: 'Scheduler Alt Text Clicked',
    SCHEDULER_APPLY_BRANDS_CLICKED: 'Scheduler Apply Brands Clicked',
    SCHEDULER_APPLY_TO_ALL_CLICKED: 'Scheduler Apply to All Clicked',
    SCHEDULER_ASSET_SCHEDULER_RESTRICTION: 'Scheduler Asset Scheduler Restriction',
    SCHEDULER_BULK_CREATE_POST: 'Scheduler Bulk Create Post',
    SCHEDULER_BULK_SAVE: 'Scheduler Bulk Save',
    SCHEDULER_BULK_UPDATE: 'Scheduler Bulk Update',
    SCHEDULER_CHANNEL_FILTER: 'Scheduler Channel Filter',
    SCHEDULER_CUSTOM_COVER_IMAGE: 'Scheduler Custom Cover Image',
    SCHEDULER_QR_CODE_NOTIFICATION: 'Scheduler QR Code Notification',
    SCHEDULER_UPDATE_CLICKED: 'Scheduler Update Clicked',
  },
  SETTINGS: {
    SETTINGS_ACCOUNT_TAG_CREATED: 'Settings Account Tag Created',
    SETTINGS_ACCOUNT_TAG_DELETED: 'Settings Account Tag Deleted',
    SETTINGS_ACCOUNT_TAG_UPDATED: 'Settings Account Tag Updated',
    SETTINGS_ADD_COMPETITOR_CLICKED: 'Settings Add Competitor Clicked',
  },
  SOCIAL_LISTENING: {
    SOCIAL_LISTENING_BANNER_CTA_CLICK: 'Social Listening Banner CTA Click',
    SOCIAL_LISTENING_BANNER_DISMISSED: 'Social Listening Banner Dismissed',
    SOCIAL_LISTENING_EMAIL_PREFERENCES_SEND_FREQUENCY_SAVED:
      'Social Listening Email Preferences - Send Frequency Saved',
    TERM_DOC_UPLOADED: 'Term Doc Uploaded',
    UPDATE_SENTIMENT: 'Update Sentiment',
    REQUESTED_BY_FILTER_USED: '“Requested by” Filter Used',
  },
  TIKTOK: {
    TIKTOK_CONTENT_SELECTION: 'TikTok Content Selection',
    TIKTOK_OVERVIEW_FILTER: 'TikTok Overview Filter',
    TIKTOK_TRENDING_SOUND_SELECTION: 'TikTok Trending Sound Selection',
    TRENDING_SOUND_PAGE_ACTION: 'Trending Sound Page Action',
    TRENDING_SOUND_PAGE_SCHEDULED_SONG: 'Trending Sound Page Scheduled Song',
    TRENDING_SOUND_SELECTED: 'Trending Sound Selected',
    TRENDING_SOUND_UPDATES: 'Trending Sound Updates',
  },
  TICKETS: {
    VIEW_TICKET_SELECTED: 'View Ticket Selected',
  },
  TWITTER: {
    TWITTER_OVERVIEW_FILTER: 'Twitter Overview Filter',
    TWITTER_SCHEDULED_POST_MENTION_WARNING: 'Twitter Scheduled Post Mention Warning',
  },
  UTM: {
    UTM_MANAGEMENT_KEY_UPDATE: 'UTM Management - Key Update',
    UTM_MANAGEMENT_PRESET_LIST: 'UTM Management - Preset List',
    UTM_MANAGEMENT_PRESET_VALUE_UPDATED: 'UTM Management - Preset Value Updated',
    UTM_MANAGEMENT_UTM_UPDATE: 'UTM Management - UTM Update',
  },
  VISION_AI: {
    AI_SUGGESTED_POSTS_ADDED: 'AI Suggested Posts Added',
    AI_SUGGESTED_POSTS_SELECTED: 'AI Suggested Posts Selected',
    ADOBE_EXPRESS_EDITOR_OPENED: 'Adobe Express Editor - Opened',
    GENERATE_AI_OUTPUT: 'Generate AI Output',
    PROMPT_PROJECT_GENERATE_BUTTON_CLICK: 'Prompt Project Generate Button Click',
    VISION_MODEL_UPDATED: 'Vision Model Updated',
  },
} as const;

/** A group of related analytics events, keyed by SCREAMING_SNAKE_CASE constant. */
type AnalyticsEventGroup = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

/** Union of every registered analytics event name string. */
export type AnalyticsEventName = AnalyticsEventGroup[keyof AnalyticsEventGroup];

/**
 * Loosely-typed event name accepted by `track()` and friends.
 *
 * Resolves to `AnalyticsEventName` for autocomplete while still allowing any
 * string — so existing call sites passing raw or dynamically-built names keep
 * compiling. `(string & {})` preserves the literal autocomplete suggestions —
 * the redundancy is intentional, so the lint rule is disabled here.
 */
// oxlint-disable-next-line typescript/no-redundant-type-constituents
export type TrackableEventName = AnalyticsEventName | (string & {});

/** Every registered event name, flattened — useful for runtime validation/tests. */
export const ALL_ANALYTICS_EVENT_NAMES: readonly AnalyticsEventName[] = Object.values(
  ANALYTICS_EVENTS,
).flatMap((group) => Object.values(group)) as AnalyticsEventName[];
